// 📖 Policies (v128.3) — the JACK-memory policy library, readable in BOTH apps.
//
// Renders the mirrored `policies` table (v128.0: JACK Supabase → Worker sync →
// our DB; JACK memory stays the ONLY upload/edit surface). Product rules
// (Matt, 2026-07-29): visible to EVERYONE at every level; text-only render
// (the email lesson: escape + linkify, never raw HTML); each policy links its
// original uploaded document (file_url) when one exists.
//
// One bundle, two hosts:
//   - staff app  (index.html):  #policiesRoot   — reached from the News
//     notice board's 📖 Policies link (no bottom-nav button, the surveys/logs
//     pattern) via switchScreen('policies'); reads with ?store= (v125.51
//     read-before-pick — no PIN to READ policy, same as news).
//   - admin app  (admin.html):  #adminPoliciesRoot — the 📖 Policies tab;
//     reads on the Bearer.
//
// Bare-name globals (DB, currentStore, escapeHtml…) are typeof-guarded — the
// classic-script bundle contract.

function esc(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function api(path, opts) {
  opts = opts || {};
  var base = (typeof DB !== 'undefined' && DB.workerUrl) ? DB.workerUrl() : '';
  var headers = { 'Content-Type': 'application/json' };
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) headers['Authorization'] = 'Bearer ' + tk;
  return fetch(base + path, { method: opts.method || 'GET', headers: headers, body: opts.body, cache: 'no-store' });
}

// Read identity for the staff app: the signed-in store rides as ?store=
// (Worker validates it against the stores table). Accounts carry the Bearer
// instead — api() already attached it, so no param is needed.
function readIdQ() {
  var tk = (typeof DB !== 'undefined' && DB.getToken) ? DB.getToken() : null;
  if (tk) return '';
  var store = (typeof currentStore !== 'undefined' && currentStore) ? String(currentStore) : '';
  return store ? ('?store=' + encodeURIComponent(store)) : '';
}

// Escape FIRST, then turn bare URLs into links — the only HTML we ever emit
// ourselves. Newlines become paragraph breaks. Never raw HTML from the body.
function renderBody(text) {
  // v129.1 — the shared RichText layer supplies inline markers + links on the
  // ESCAPED text (guarded; fallback = plain escape). The paragraph model
  // (blank-line splits -> <p>) stays OURS — it reads better for long policies.
  var safe = esc(text);
  var useRich = (typeof RichText !== 'undefined' && RichText.applyInline);
  if (!useRich) {
    safe = safe.replace(/(https?:\/\/[^\s<]+)/g, function (u) {
      var t = u.replace(/[.,)\]!?]+$/, '');
      return '<a href="' + t + '" target="_blank" rel="noopener">' + t + '</a>' + u.slice(t.length);
    });
  }
  return safe.split(/\n{2,}/).map(function (par) {
    var body = useRich ? RichText.applyInline(par) : par;
    return '<p style="margin:0 0 10px;line-height:1.55">' + body.replace(/\n/g, '<br>') + '</p>';
  }).join('');
}

function fmtDate(iso) {
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) { return ''; }
}

var _list = [];        // the fetched library (id, title, file_url, dates)
var _q = '';           // current search text
var _openId = null;    // policy open in the detail view (null = list)
var _cache = {};       // id → full policy (bodies fetched once per session)
var _loading = false;
var _error = null;
// 💬 Q&A state (v128.4) — EPHEMERAL, screen-only: the answer is never stored
// anywhere (the /api/jack/ask posture; the Worker stores nothing either).
var _askBusy = false;
var _askAnswer = null; // { answer, sources, fileUrls, degraded }

function root() {
  return document.getElementById('policiesRoot') || document.getElementById('adminPoliciesRoot');
}

async function load(force) {
  if (_loading) return;
  if (_list.length && !force) { paint(); return; }
  _loading = true; _error = null; paint();
  try {
    var res = await api('/api/policies' + readIdQ());
    var data = await res.json().catch(function () { return null; });
    if (!res.ok || !data || data.ok === false) {
      _error = (data && data.error) || ('Could not load policies (HTTP ' + res.status + ')');
      _list = [];
    } else {
      _list = data.policies || [];
    }
  } catch (e) {
    _error = 'Could not load policies — check your connection and try again.';
  }
  _loading = false;
  paint();
}

async function open(id) {
  _openId = id;
  if (!_cache[id]) {
    paint();   // show the loading shell while the body fetches
    try {
      var res = await api('/api/policies/' + id + readIdQ());
      var data = await res.json().catch(function () { return null; });
      if (res.ok && data && data.policy) _cache[id] = data.policy;
      else _cache[id] = { id: id, title: 'Policy', body: '', _error: (data && data.error) || ('HTTP ' + res.status) };
    } catch (e) {
      _cache[id] = { id: id, title: 'Policy', body: '', _error: 'Network error — try again.' };
    }
  }
  paint();
  var el = root(); if (el) el.scrollTop = 0;
  try { window.scrollTo(0, 0); } catch (e) { /* embedded hosts */ }
}

function back() { _openId = null; paint(); }

function search(q) {
  _q = String(q == null ? '' : q).toLowerCase();
  paintListOnly();
}

function filtered() {
  if (!_q) return _list;
  return _list.filter(function (p) { return String(p.title || '').toLowerCase().indexOf(_q) !== -1; });
}

function listHtml() {
  var rows = filtered();
  if (_loading) return '<div style="padding:24px;text-align:center;color:var(--sub,#667)">Loading the policy library…</div>';
  if (_error) return '<div style="padding:18px;color:var(--danger-deep);font-size:13px">⚠ ' + esc(_error) + ' <a href="#" onclick="Policies.reload();return false">Retry</a></div>';
  if (!_list.length) return '<div style="padding:24px;text-align:center;color:var(--sub,#667)">No policies in the library yet.<br><span style="font-size:12px">Head Office syncs them from JACK memory (Dev tab → Policy mirror).</span></div>';
  if (!rows.length) return '<div style="padding:18px;text-align:center;color:var(--sub,#667)">No titles match &ldquo;' + esc(_q) + '&rdquo;.</div>';
  return rows.map(function (p) {
    return '<div onclick="Policies.open(' + p.id + ')" style="background:var(--card,#fff);border:1px solid var(--border);border-radius:12px;padding:12px 14px;margin-bottom:8px;cursor:pointer;display:flex;align-items:center;gap:10px">' +
      '<div style="font-size:20px">📄</div>' +
      '<div style="min-width:0;flex:1">' +
        '<div translate="no" style="font-weight:700;font-size:14px">' + esc(p.title) + '</div>' +
        (p.source_updated_at ? '<div style="font-size:11.5px;color:var(--sub,#667)">Updated ' + fmtDate(p.source_updated_at) + '</div>' : '') +
      '</div>' +
      '<div style="color:var(--sub,#99a);font-size:18px">›</div>' +
    '</div>';
  }).join('');
}

function detailHtml() {
  var p = _cache[_openId];
  if (!p) return '<div style="padding:24px;text-align:center;color:var(--sub,#667)">Loading…</div>';
  var body = p._error
    ? '<div style="color:var(--danger-deep);font-size:13px">⚠ ' + esc(p._error) + '</div>'
    : (p.body ? renderBody(p.body) : '<div style="color:var(--sub,#667);font-size:13px">This policy has no extracted text — open the original document below.</div>');
  return '<div style="background:var(--card,#fff);border:1px solid var(--border);border-radius:12px;padding:16px">' +
    '<div translate="no" style="font-size:17px;font-weight:800;margin-bottom:2px">' + esc(p.title) + '</div>' +
    '<div style="font-size:11.5px;color:var(--sub,#667);margin-bottom:12px">' +
      (p.source_updated_at ? 'Updated ' + fmtDate(p.source_updated_at) + ' · ' : '') +
      (p.active === false ? '<span style="color:var(--ui-warn)">no longer current — kept for reference</span> · ' : '') +
      'from the JACK policy library' +
    '</div>' +
    '<div style="font-size:13.5px;color:var(--text)"' + (p._error || !p.body ? '' : ' translate="no"') + '>' + body + '</div>' +
    (p.file_url ? '<a href="#" onclick="Policies.openFile(' + Number(p.id) + ');return false" style="display:inline-block;margin-top:12px;background:var(--brand,#009E3D);color:#fff;text-decoration:none;font-size:12.5px;font-weight:700;padding:8px 14px;border-radius:8px">📎 Open the original document</a>' : '') +
  '</div>';
}

// ── 📎 Open the original document (v128.45) ──────────────────────────────────
// The raw file_url is JACK's PRIVATE-bucket storage link — it 404s
// ("Bucket not found") for everyone (the Marina Mall 31 Jul bug). The Worker
// proxies the object with its JACK key at /api/policies/:id/file; we fetch it
// as a blob (token stays in headers, never in a URL) and show it in a tab
// opened SYNCHRONOUSLY on the tap so popup blockers stay quiet.
// ── v129.3 (Matt: "WISH IT WOULD SHOW AS A PDF - I DONT LIKE THAT IT
// DOWNLOADS WORK DOCS") — Word documents render IN-APP: the vendored
// mammoth.js (vendor/, OUR origin — no CDN, HR documents never touch a third
// party) converts the .docx to HTML client-side, shown in a fully sandboxed
// iframe (sandbox="" — nothing can run). PDFs keep the native browser preview
// tab; legacy .doc (pre-2007, mammoth can't read it) keeps the download path.
// Any viewer failure falls back to the old behaviour — never a dead end.
var _mammothLoading = null;
function loadMammoth() {
  if (typeof window.mammoth !== 'undefined') return Promise.resolve(true);
  if (_mammothLoading) return _mammothLoading;
  _mammothLoading = new Promise(function (resolve) {
    var s = document.createElement('script');
    s.src = 'vendor/mammoth.browser.min.js';
    var to = setTimeout(function () { resolve(false); }, 10000);
    s.onload = function () { clearTimeout(to); resolve(typeof window.mammoth !== 'undefined'); };
    s.onerror = function () { clearTimeout(to); resolve(false); };
    document.head.appendChild(s);
  });
  return _mammothLoading;
}

function docxClose() {
  var el = document.getElementById('polDocxOverlay');
  if (el) el.remove();
}

function showDocxOverlay(html, title, objUrl) {
  docxClose();
  var wrap = document.createElement('div');
  wrap.id = 'polDocxOverlay';
  wrap.style.cssText = 'position:fixed;inset:0;background:rgba(15,23,42,.5);z-index:960;display:flex;align-items:center;justify-content:center;padding:12px';
  // v136 dark mode: the document itself stays white paper in both themes (its iframe cannot see the app variables).
  var doc = '<!doctype html><html><head><meta charset="utf-8"><style>' +
    'body{font-family:-apple-system,system-ui,Segoe UI,sans-serif;font-size:14.5px;line-height:1.6;color:#111827;margin:0;padding:22px 26px;background:#fff}' +
    'h1,h2,h3{line-height:1.3} table{border-collapse:collapse;max-width:100%} td,th{border:1px solid #d1d5db;padding:5px 8px;font-size:13px}' +
    'img{max-width:100%} p{margin:0 0 10px}' +
    '</style></head><body>' + html + '</body></html>';
  wrap.innerHTML =
    '<div style="background:var(--card);border-radius:16px;max-width:820px;width:100%;height:92vh;display:flex;flex-direction:column;overflow:hidden">' +
      '<div style="display:flex;align-items:center;gap:10px;padding:10px 14px;border-bottom:1px solid var(--border)">' +
        '<div style="font-weight:800;font-size:14px;flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">📄 <span' + (title ? ' translate="no"' : '') + '>' + esc(title || 'Document') + '</span></div>' +
        '<a href="' + objUrl + '" download style="font-size:12px;font-weight:700;color:var(--brand,#009E3D);text-decoration:none">Download</a>' +
        '<button onclick="Policies.docxClose()" aria-label="Close" style="border:none;background:var(--surface-3);border-radius:8px;width:30px;height:30px;font-size:15px;cursor:pointer">✕</button>' +
      '</div>' +
      '<iframe sandbox="" style="border:none;flex:1;width:100%"></iframe>' +
    '</div>';
  document.body.appendChild(wrap);
  // srcdoc set via the property AFTER insert — the html stays data, not markup
  wrap.querySelector('iframe').srcdoc = doc;
}

async function openFile(id) {
  var tab = null;
  try { tab = window.open('', '_blank'); } catch (e) { /* blocked — fall through */ }
  if (tab) { try { tab.document.write('Loading the document…'); } catch (e) { /* cross-origin later */ } }
  try {
    var res = await api('/api/policies/' + id + '/file' + readIdQ());
    if (!res.ok) {
      var data = await res.json().catch(function () { return null; });
      throw new Error((data && data.error) || ('HTTP ' + res.status));
    }
    var blob = await res.blob();
    var objUrl = URL.createObjectURL(blob);
    // modern Word files render in-app; everything else keeps the tab
    if (String(blob.type).indexOf('wordprocessingml') !== -1 && (await loadMammoth())) {
      try {
        var buf = await blob.arrayBuffer();
        var out = await window.mammoth.convertToHtml({ arrayBuffer: buf });
        var row = _list.filter(function (p) { return String(p.id) === String(id); })[0];
        if (tab) { try { tab.close(); } catch (e3) { /* already gone */ } }
        showDocxOverlay(out.value || '<p>(empty document)</p>', row && row.title, objUrl);
        return;
      } catch (eConv) { /* fall through to the tab — never a dead end */ }
    }
    if (tab) tab.location = objUrl;
    else window.location = objUrl;
  } catch (e) {
    if (tab) { try { tab.close(); } catch (e2) { /* already gone */ } }
    var msg = 'Could not open the document — ' + ((e && e.message) || 'try again');
    if (typeof showToast === 'function') showToast(msg, 'error');
    else alert(msg);
  }
}

// ── 💬 Ask the policies (v128.4) ──────────────────────────────────────────────
async function ask() {
  var input = document.getElementById('polAskInput');
  var q = input ? String(input.value || '').trim() : '';
  if (!q || _askBusy) return;
  _askBusy = true; _askAnswer = null; paintAskOnly();
  try {
    var res = await api('/api/policies/ask' + readIdQ(), { method: 'POST', body: JSON.stringify({ question: q }) });
    var data = await res.json().catch(function () { return null; });
    if (res.ok && data && data.answer) _askAnswer = data;
    else _askAnswer = { answer: (data && data.error) || 'Sorry — I could not answer that right now. Try again in a moment.', degraded: true };
  } catch (e) {
    _askAnswer = { answer: 'Network hiccup — check your connection and try again.', degraded: true };
  }
  _askBusy = false;
  paintAskOnly();
  // keep the question in the box so a retry is one tap
  var again = document.getElementById('polAskInput');
  if (again) again.value = q;
}

function askHtml() {
  var inner;
  if (_askBusy) inner = '<div style="padding:10px 0;color:var(--sub,#667);font-size:13px">💭 Checking the policies…</div>';
  else if (_askAnswer) {
    var a = _askAnswer;
    var files = '';
    if (Array.isArray(a.fileUrls) && a.fileUrls.length) {
      // v128.45: raw JACK storage links 404 (private bucket) — route each
      // through the Worker proxy via its mirrored policy; drop unmatched URLs
      // (a dead chip is worse than none).
      var chips = a.fileUrls.slice(0, 5).map(function (u) {
        var match = _list.filter(function (p) { return p.file_url && String(p.file_url) === String(u); })[0];
        if (!match) return '';
        return '<a href="#" translate="no" onclick="Policies.openFile(' + Number(match.id) + ');return false" style="font-size:11.5px;background:var(--gp);border:1px solid var(--learn-green-line);border-radius:8px;padding:4px 10px;text-decoration:none;color:var(--ok-deep);font-weight:700">📎 ' + esc(String(match.title || 'Source document').slice(0, 40)) + '</a>';
      }).filter(Boolean);
      if (chips.length) files = '<div style="display:flex;flex-wrap:wrap;gap:6px;margin-top:8px">' + chips.join('') + '</div>';
    }
    inner = '<div style="background:var(--surface-2);border:1px solid var(--learn-green-line);border-radius:10px;padding:10px 12px;margin-top:8px;font-size:13px;line-height:1.5">' +
      (a.degraded ? renderBody(a.answer) : '<div translate="no">' + renderBody(a.answer) + '</div>') + files +   // v135.1: Jack's answer is never re-translated; our own fallback lines are
      (a.degraded ? '<div style="font-size:11px;color:var(--ui-warn);margin-top:6px">⚠ Answered without the full policy brain — double-check with your manager.</div>' : '') +
    '</div>';
  } else inner = '';
  return '<div style="background:var(--card,#fff);border:1px solid var(--border);border-radius:12px;padding:10px 12px;margin-bottom:10px">' +
    '<div style="font-size:12.5px;font-weight:700;margin-bottom:6px">💬 Ask the policies</div>' +
    '<div style="display:flex;gap:6px">' +
      '<input id="polAskInput" type="text" maxlength="600" placeholder="e.g. How many sick days before a certificate is needed?" ' +
        'onkeydown="if(event.key===\'Enter\')Policies.ask()" ' +
        'style="flex:1;min-width:0;padding:9px 11px;border:1px solid var(--border-strong);border-radius:9px;font-size:13px">' +
      '<button onclick="Policies.ask()" style="background:var(--brand,#009E3D);color:#fff;border:none;border-radius:9px;padding:9px 14px;font-size:13px;font-weight:700;cursor:pointer">Ask</button>' +
    '</div>' +
    '<div id="polAskOut">' + inner + '</div>' +
    '<div style="font-size:10.5px;color:var(--sub,#99a);margin-top:6px">Answers come from the policy library and are not saved anywhere.</div>' +
  '</div>';
}

function paintAskOnly() {
  var out = document.getElementById('polAskOut');
  if (!out) { paint(); return; }
  var shell = document.createElement('div');
  shell.innerHTML = askHtml();
  var fresh = shell.querySelector('#polAskOut');
  out.innerHTML = fresh ? fresh.innerHTML : '';
}

function paintListOnly() {
  var box = document.getElementById('polListBox');
  if (box) box.innerHTML = listHtml(); else paint();
}

function paint() {
  var el = root();
  if (!el) return;
  if (_openId != null) {
    el.innerHTML =
      '<div style="max-width:760px;margin:0 auto">' +
        '<a href="#" onclick="Policies.back();return false" style="display:inline-block;margin:2px 0 10px;font-size:13px;font-weight:700;color:var(--brand,#009E3D);text-decoration:none">‹ All policies</a>' +
        detailHtml() +
      '</div>';
    return;
  }
  el.innerHTML =
    '<div style="max-width:760px;margin:0 auto">' +
      '<div style="display:flex;align-items:center;gap:10px;margin-bottom:10px">' +
        '<div style="font-size:22px">📖</div>' +
        '<div><div style="font-size:17px;font-weight:800">Policies</div>' +
        '<div style="font-size:11.5px;color:var(--sub,#667)">Company policy library — synced from JACK memory</div></div>' +
      '</div>' +
      askHtml() +
      '<input id="polSearch" type="search" placeholder="🔍 Search policies…" value="' + esc(_q) + '" oninput="Policies.search(this.value)" ' +
        'style="width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid var(--border-strong);border-radius:10px;font-size:14px;margin-bottom:10px">' +
      '<div id="polListBox">' + listHtml() + '</div>' +
    '</div>';
}

// The screen/tab entry point for BOTH hosts.
function render() {
  _openId = null;
  paint();
  load(false);
}

function reload() { _list = []; _cache = {}; load(true); }

window.Policies = {
  render: render,
  open: open,
  back: back,
  search: search,
  reload: reload,
  ask: ask,
  openFile: openFile,
  docxClose: docxClose,
};

export { render, open, back, search, reload, ask, openFile, docxClose };
